import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Breadcrumbs from '../../components/Breadcrumbs';

const PageContainer = styled.div`
  padding-top: 80px;
`;

const Hero = styled.section`
  position: relative;
  min-height: 700px;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: white;
  overflow: hidden;
  display: flex;
  align-items: center;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(147, 197, 253, 0.15) 0%, transparent 70%),
      radial-gradient(circle at 80% 70%, rgba(96, 165, 250, 0.1) 0%, transparent 70%);
    opacity: 0.8;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
  padding: 8rem 2rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 4rem;
    padding: 6rem 2rem;
  }
`;

const HeroText = styled.div``;

const SecurityBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  background: rgba(147, 197, 253, 0.1);
  color: #93c5fd;
  border-radius: 30px;
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(147, 197, 253, 0.2);
  backdrop-filter: blur(10px);
`;

const Title = styled(motion.h1)`
  font-size: 4rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #93c5fd 0%, #60a5fa 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: #94a3b8;
  margin-bottom: 2.5rem;
  line-height: 1.7;
  opacity: 0.9;
`;

const SecurityShield = styled(motion.div)`
  position: relative;
  width: 500px;
  height: 500px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8rem;

  &:before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(147, 197, 253, 0.1) 0%, rgba(96, 165, 250, 0.05) 100%);
    border-radius: 50%;
    animation: pulse 3s infinite;
  }

  &:after {
    content: '';
    position: absolute;
    inset: -20px;
    background: linear-gradient(135deg, rgba(147, 197, 253, 0.05) 0%, rgba(96, 165, 250, 0.02) 100%);
    border-radius: 50%;
    animation: pulse 3s infinite 1.5s;
  }

  @keyframes pulse {
    0% {
      transform: scale(0.95);
      opacity: 0.5;
    }
    50% {
      transform: scale(1.05);
      opacity: 0.2;
    }
    100% {
      transform: scale(0.95);
      opacity: 0.5;
    }
  }

  @media (max-width: 768px) {
    width: 300px;
    height: 300px;
    font-size: 5rem;
  }
`;

const SecurityIcon = styled(motion.div)`
  position: relative;
  z-index: 2;
  width: 180px;
  height: 180px;
  background: linear-gradient(135deg, #93c5fd 0%, #60a5fa 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 
    0 0 0 20px rgba(147, 197, 253, 0.1),
    0 0 0 40px rgba(147, 197, 253, 0.05),
    0 10px 30px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    width: 120px;
    height: 120px;
  }
`;

const FeaturesSection = styled.section`
  padding: 8rem 2rem;
  background: white;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(147, 197, 253, 0.05) 0%, transparent 70%),
      radial-gradient(circle at 80% 70%, rgba(96, 165, 250, 0.05) 0%, transparent 70%);
  }
`;

const FeatureGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 3rem;
  position: relative;
  z-index: 1;

  &:before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 800px;
    height: 800px;
    background: radial-gradient(circle at center, rgba(147, 197, 253, 0.1) 0%, transparent 70%);
    z-index: -1;
    border-radius: 50%;
  }

  &:after {
    content: '';
    position: absolute;
    bottom: -50%;
    left: -50%;
    width: 800px;
    height: 800px;
    background: radial-gradient(circle at center, rgba(96, 165, 250, 0.1) 0%, transparent 70%);
    z-index: -1;
    border-radius: 50%;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FeatureCard = styled(motion.div)`
  display: flex;
  gap: 2rem;
  padding: 2.5rem;
  background: white;
  border-radius: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(147, 197, 253, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.1);
    border-color: rgba(147, 197, 253, 0.2);
  }
`;

const FeatureIcon = styled.div`
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  background: linear-gradient(135deg, rgba(147, 197, 253, 0.1) 0%, rgba(96, 165, 250, 0.05) 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  transition: all 0.3s ease;

  ${FeatureCard}:hover & {
    background: linear-gradient(135deg, rgba(147, 197, 253, 0.2) 0%, rgba(96, 165, 250, 0.1) 100%);
    transform: scale(1.1);
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
  line-height: 1.3;
`;

const FeatureDescription = styled.p`
  color: #64748b;
  line-height: 1.7;
  font-size: 1.1rem;
`;

const FeatureContent = styled.div`
  flex: 1;
`;

const ComplianceSection = styled.section`
  padding: 8rem 2rem;
  background: #f8fafc;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(147, 197, 253, 0.08) 0%, transparent 70%),
      radial-gradient(circle at 80% 70%, rgba(96, 165, 250, 0.08) 0%, transparent 70%),
      url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%232563eb' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }
`;

const ComplianceGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2.5rem;
  position: relative;
  z-index: 1;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ComplianceBadge = styled(motion.div)`
  background: white;
  padding: 2.5rem;
  border-radius: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(147, 197, 253, 0.1);
  text-align: center;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #93c5fd 0%, #60a5fa 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.1);
    border-color: rgba(147, 197, 253, 0.2);

    &:before {
      opacity: 1;
    }

    .icon-wrapper {
      background: linear-gradient(135deg, rgba(147, 197, 253, 0.2) 0%, rgba(96, 165, 250, 0.1) 100%);
      transform: scale(1.1);
    }
  }

  .icon-wrapper {
    width: 64px;
    height: 64px;
    background: linear-gradient(135deg, rgba(147, 197, 253, 0.1) 0%, rgba(96, 165, 250, 0.05) 100%);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    transition: all 0.3s ease;
  }

  .badge-name {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1e293b;
  }

  .badge-description {
    font-size: 0.95rem;
    color: #64748b;
    line-height: 1.6;
    font-weight: normal;
  }

  .badge-features {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(147, 197, 253, 0.1);
    width: 100%;
    text-align: left;
  }

  .feature-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #64748b;
    font-size: 0.9rem;
    font-weight: normal;
    margin-bottom: 0.5rem;

    &:last-child {
      margin-bottom: 0;
    }

    svg {
      color: #60a5fa;
      flex-shrink: 0;
    }
  }
`;

const CTASection = styled.section`
  padding: 8rem 2rem;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(147, 197, 253, 0.15) 0%, transparent 70%),
      radial-gradient(circle at 80% 70%, rgba(96, 165, 250, 0.1) 0%, transparent 70%);
  }
`;

const CTAContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CTATitle = styled.h2`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #93c5fd 0%, #60a5fa 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const CTAText = styled.p`
  font-size: 1.25rem;
  color: #94a3b8;
  margin-bottom: 3rem;
  line-height: 1.7;
  max-width: 600px;
  opacity: 0.9;
`;

const CTAButton = styled(motion.button)`
  background: linear-gradient(135deg, #93c5fd 0%, #60a5fa 100%);
  color: white;
  padding: 1.5rem 4rem;
  border-radius: 50px;
  font-size: 1.25rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.2);
  }
`;

// Add new styled components for enhanced hero section
const HeroStats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 4rem;
  max-width: 900px;
  margin: 4rem auto 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const HeroStat = styled(motion.div)`
  color: #94a3b8;
  text-align: center;

  strong {
    display: block;
    color: white;
    font-size: 2.5rem;
    font-weight: 800;
    margin-bottom: 0.5rem;
    background: linear-gradient(135deg, #93c5fd 0%, #60a5fa 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

// Add section header components
const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 5rem;
  position: relative;
  z-index: 1;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 1rem;
  
  span {
    background: linear-gradient(135deg, #93c5fd 0%, #60a5fa 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  &.light {
    color: white;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.25rem;
  color: #64748b;
  line-height: 1.7;
  max-width: 600px;
  margin: 0 auto;

  &.light {
    color: #94a3b8;
  }
`;

// Add section CTA button
const SectionCTAButton = styled(motion.button)`
  background: linear-gradient(135deg, #93c5fd 0%, #60a5fa 100%);
  color: white;
  padding: 1rem 2.5rem;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 6px rgba(147, 197, 253, 0.2);
  margin-top: 3rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 8px rgba(147, 197, 253, 0.3);
  }
`;

// Add social proof section components
const SocialProofSection = styled.section`
  padding: 8rem 2rem;
  background: #f8fafc;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(147, 197, 253, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(147, 197, 253, 0.1) 0%, transparent 50%),
      url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%232563eb' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }
`;

const TestimonialsGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto 4rem;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TestimonialCard = styled(motion.div)`
  background: white;
  padding: 2.5rem;
  border-radius: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(147, 197, 253, 0.1);
`;

const TestimonialContent = styled.p`
  color: #1e293b;
  font-size: 1.125rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  font-style: italic;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const AuthorAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #93c5fd 0%, #60a5fa 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
`;

const AuthorInfo = styled.div``;

const AuthorName = styled.div`
  color: #1e293b;
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const AuthorRole = styled.div`
  color: #64748b;
  font-size: 0.875rem;
`;

const CompanyLogos = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 4rem;
`;

const CompanyLogo = styled(motion.div)`
  color: #64748b;
  font-weight: 700;
  font-size: 1.25rem;
  letter-spacing: 0.1em;
  opacity: 0.8;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 1;
  }
`;

const TrustIndicators = styled.div`
  max-width: 1200px;
  margin: 4rem auto 0;
  padding-top: 4rem;
  border-top: 1px solid rgba(147, 197, 253, 0.2);
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 3rem;
  text-align: center;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const TrustIndicator = styled(motion.div)`
  .number {
    font-size: 3rem;
    font-weight: 800;
    color: #1e293b;
    margin-bottom: 0.5rem;
    background: linear-gradient(135deg, #93c5fd 0%, #60a5fa 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .label {
    color: #64748b;
    font-size: 1.1rem;
    line-height: 1.5;
  }
`;

function SecurityTesting() {
  return (
    <PageContainer>
      <Breadcrumbs items={[
        { label: 'Services', path: '/services' },
        { label: 'Security Testing', path: '/services/security' }
      ]} />
      <Hero>
        <HeroContent>
          <HeroText>
            <SecurityBadge
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              🔒 Enterprise Security Testing
            </SecurityBadge>
            <Title
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Protect Your Business with Advanced Security Testing
            </Title>
            <Subtitle>
              Comprehensive security testing and monitoring to protect your store from threats,
              ensure compliance, and maintain customer trust.
            </Subtitle>
            <CTAButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get Free Security Audit
            </CTAButton>
          </HeroText>
          <SecurityShield
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ 
              duration: 0.8, 
              delay: 0.4,
              type: "spring",
              stiffness: 100
            }}
          >
            <SecurityIcon
              initial={{ rotate: -180, scale: 0 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{
                duration: 1,
                delay: 0.6,
                type: "spring",
                stiffness: 100
              }}
            >
              <svg 
                width="80" 
                height="80" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <path d="M8 11l3 3 5-5"/>
              </svg>
            </SecurityIcon>
          </SecurityShield>
          <HeroStats>
            {[
              { value: '100%', label: 'PCI DSS Compliance' },
              { value: '24/7', label: 'Threat Monitoring' },
              { value: '0', label: 'Security Breaches' }
            ].map((stat, index) => (
              <HeroStat
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
              >
                <strong>{stat.value}</strong>
                {stat.label}
              </HeroStat>
            ))}
          </HeroStats>
        </HeroContent>
      </Hero>

      <FeaturesSection>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            Comprehensive <span>Security Features</span>
          </SectionTitle>
          <SectionSubtitle>
            Enterprise-grade security testing solutions to protect your store and customer data
          </SectionSubtitle>
        </SectionHeader>
        <FeatureGrid>
          {[
            {
              icon: '🔍',
              title: 'Vulnerability Assessment',
              description: 'Regular automated and manual scans to identify security vulnerabilities, including SQL injection, XSS, and CSRF attacks.'
            },
            {
              icon: '🔐',
              title: 'Access Control Testing',
              description: 'Comprehensive testing of user permissions, role-based access control, and authentication mechanisms to prevent unauthorized access.'
            },
            {
              icon: '🔒',
              title: 'Data Encryption',
              description: 'Verify end-to-end encryption of sensitive data, SSL/TLS implementation, and secure key management practices.'
            },
            {
              icon: '⚡',
              title: 'Real-time Monitoring',
              description: 'Advanced threat detection system with 24/7 monitoring, instant alerts, and automated incident response procedures.'
            },
            {
              icon: '🛡️',
              title: 'Penetration Testing',
              description: 'Professional white-hat hackers perform controlled cyber attacks to identify and fix security weaknesses before they can be exploited.'
            },
            {
              icon: '📊',
              title: 'Security Analytics',
              description: 'Advanced AI-powered analytics to detect patterns, predict potential threats, and provide actionable security insights.'
            },
            {
              icon: '🔄',
              title: 'Continuous Security',
              description: 'Automated security testing integrated into your development pipeline to catch vulnerabilities before deployment.'
            },
            {
              icon: '📱',
              title: 'Mobile Security',
              description: 'Specialized security testing for mobile commerce, including app security, API testing, and mobile payment validation.'
            }
          ].map((feature, index) => (
            <FeatureCard
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <FeatureIcon>{feature.icon}</FeatureIcon>
              <FeatureContent>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureContent>
            </FeatureCard>
          ))}
        </FeatureGrid>
        <div style={{ textAlign: 'center', marginTop: '4rem' }}>
          <SectionCTAButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Explore Security Features
          </SectionCTAButton>
        </div>
      </FeaturesSection>

      <ComplianceSection>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            Industry <span>Compliance</span>
          </SectionTitle>
          <SectionSubtitle>
            Comprehensive security testing that ensures compliance with all major industry standards
          </SectionSubtitle>
        </SectionHeader>
        <ComplianceGrid>
          {[
            {
              badge: 'PCI DSS',
              icon: '🔒',
              description: 'Payment Card Industry Data Security Standard',
              features: [
                'Regular security assessments',
                'Secure network architecture',
                'Encryption of cardholder data',
                'Vulnerability management'
              ]
            },
            {
              badge: 'GDPR',
              icon: '🇪🇺',
              description: 'General Data Protection Regulation',
              features: [
                'Data protection by design',
                'Privacy impact assessments',
                'Consent management',
                'Data breach protection'
              ]
            },
            {
              badge: 'ISO 27001',
              icon: '✅',
              description: 'Information Security Management',
              features: [
                'Risk assessment framework',
                'Security controls testing',
                'Incident management',
                'Continuous monitoring'
              ]
            },
            {
              badge: 'SOC 2',
              icon: '🛡️',
              description: 'Service Organization Control 2',
              features: [
                'Security monitoring',
                'Access control testing',
                'Data encryption verification',
                'Compliance reporting'
              ]
            }
          ].map((compliance, index) => (
            <ComplianceBadge
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="icon-wrapper">{compliance.icon}</div>
              <div className="badge-name">{compliance.badge}</div>
              <div className="badge-description">{compliance.description}</div>
              <div className="badge-features">
                {compliance.features.map((feature, featureIndex) => (
                  <div key={featureIndex} className="feature-item">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {feature}
                  </div>
                ))}
              </div>
            </ComplianceBadge>
          ))}
        </ComplianceGrid>
        <div style={{ textAlign: 'center', marginTop: '4rem' }}>
          <SectionCTAButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            View Compliance Details
          </SectionCTAButton>
        </div>
      </ComplianceSection>

      <SocialProofSection>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            Trusted by <span>Industry Leaders</span>
          </SectionTitle>
          <SectionSubtitle>
            Join thousands of businesses that trust us with their security testing needs
          </SectionSubtitle>
        </SectionHeader>
        <TestimonialsGrid>
          {[
            {
              content: "Their security testing has been invaluable. We've maintained 100% PCI compliance and haven't had a single security incident since implementation. The real-time monitoring and instant alerts give us peace of mind.",
              author: "Jennifer Smith",
              role: "CISO at SecureCommerce",
              initials: "JS",
              company: "Fortune 500 Retailer"
            },
            {
              content: "The automated security testing has helped us identify and fix vulnerabilities before they could be exploited. Their platform is essential for maintaining our security posture and protecting customer data.",
              author: "Mark Thompson",
              role: "Head of Security at ShopGuard",
              initials: "MT",
              company: "Leading E-commerce Platform"
            },
            {
              content: "Implementing their security testing solution was a game-changer. We've seen a 90% reduction in security-related incidents and significantly improved our compliance posture.",
              author: "Sarah Chen",
              role: "Security Director",
              initials: "SC",
              company: "Global Retail Chain"
            }
          ].map((testimonial, index) => (
            <TestimonialCard
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <TestimonialContent>"{testimonial.content}"</TestimonialContent>
              <TestimonialAuthor>
                <AuthorAvatar>{testimonial.initials}</AuthorAvatar>
                <AuthorInfo>
                  <AuthorName>{testimonial.author}</AuthorName>
                  <AuthorRole>{testimonial.role}</AuthorRole>
                  <div style={{ color: '#94a3b8', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    {testimonial.company}
                  </div>
                </AuthorInfo>
              </TestimonialAuthor>
            </TestimonialCard>
          ))}
        </TestimonialsGrid>
        
        <CompanyLogos>
          {[
            "SHOPIFY+",
            "AMAZON",
            "WALMART",
            "ETSY",
            "EBAY",
            "BIGCOMMERCE"
          ].map((company, index) => (
            <CompanyLogo
              key={index}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 0.7 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              {company}
            </CompanyLogo>
          ))}
        </CompanyLogos>

        <TrustIndicators>
          {[
            { number: '500+', label: 'Enterprise Clients Protected' },
            { number: '99.99%', label: 'Security Uptime' },
            { number: '24/7', label: 'Security Monitoring' },
            { number: '0', label: 'Security Breaches' }
          ].map((indicator, index) => (
            <TrustIndicator
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="number">{indicator.number}</div>
              <div className="label">{indicator.label}</div>
            </TrustIndicator>
          ))}
        </TrustIndicators>
      </SocialProofSection>

      <CTASection>
        <CTAContent>
          <CTATitle>Ready to Secure Your Store?</CTATitle>
          <CTAText>
            Get a comprehensive security audit and protection plan tailored to your store.
          </CTAText>
          <CTAButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Start Free Security Audit
          </CTAButton>
        </CTAContent>
      </CTASection>
    </PageContainer>
  );
}

export default SecurityTesting;